import {
  Component,
  ChangeDetectionStrategy,
  computed,
  input,
  output,
  signal,
  forwardRef,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { AutocompleteFieldComponent } from '../autocomplete-field/autocomplete-field.component';
import { SearchCountry } from '../../../pages/donate/donate-form/donate-form.model';

type DisplayCountry = SearchCountry & { hasSeparatorAfter?: boolean };

/**
 * Latin letters whose diacritic is baked into the code point rather than added
 * as a combining mark, so NFD decomposition leaves them untouched. Mapped by
 * hand so plain-ASCII typing still matches them (e.g. "foroyar" → Føroyar,
 * "aeroe" → Ærø). Letters like Å, ç and é need no entry here — NFD splits
 * those into a base letter plus a combining mark that gets stripped.
 */
const NON_DECOMPOSABLE_LATIN: Record<string, string> = {
  'ø': 'o',
  'đ': 'd',
  'ð': 'd',
  'ł': 'l',
  'ħ': 'h',
  'ŧ': 't',
  'ı': 'i',
  'ß': 'ss',
  'æ': 'ae',
  'œ': 'oe',
  'þ': 'th',
};

/**
 * Zero-width and soft-hyphen code points that can sit inside a backend-provided
 * name without being visible. `\s` does not cover them, so they are dropped
 * explicitly alongside the whitespace fold below.
 */
const INVISIBLE_CHARACTERS = /[\u00ad\u200b-\u200d\u2060\ufeff]/g;

/**
 * Fold a country name or a search query down to a comparable ASCII-ish form:
 * lowercase, curly apostrophes flattened, special Latin letters mapped,
 * combining accents stripped via NFD, invisible characters dropped, and all
 * whitespace removed. Applied to both sides of the comparison so an accented
 * query matches an unaccented name and vice versa.
 *
 * Whitespace is dropped rather than kept because the country list comes from the
 * backend (`/get-country`), where a name can separate its words with something
 * other than a plain space — a non-breaking space ("Côte\u00a0d'Ivoire"), a stray
 * double space, or a zero-width character. Any of those made a query typed with
 * an ordinary space, such as "cote d", fail while the single word "cote" matched.
 * Removing whitespace on both sides makes the comparison independent of which
 * separator the data happens to carry.
 */
function normalizeForSearch(value: string): string {
  return value
    .toLowerCase()
    .replace(/[‘’ʼ´`]/g, "'")
    .replace(/[øđðłħŧıßæœþ]/g, char => NON_DECOMPOSABLE_LATIN[char] ?? char)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(INVISIBLE_CHARACTERS, '')
    .replace(/\s+/g, '');
}

const COUNTRY_SELECTOR_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CountrySelectorComponent),
  multi: true,
};

@Component({
  selector: 'app-country-selector',
  standalone: true,
  imports: [AutocompleteFieldComponent],
  providers: [COUNTRY_SELECTOR_VALUE_ACCESSOR],
  templateUrl: './country-selector.component.html',
  styleUrl: './country-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountrySelectorComponent implements ControlValueAccessor {
  // Inputs
  countries = input<SearchCountry[]>([]);
  required = input<boolean>(false);
  hideRequiredInfo = input<boolean>(false);
  label = input<string>('contribute.section1.country.label');
  placeholder = input<string>('contribute.section1.country.placeholder');
  errorMessage = input<string>('contribute.section1.country.error');
  helperText = input<string | null>(null);

  // Outputs
  countrySelect = output<SearchCountry>();
  hasNoValue = output<boolean>();

  // Internal state
  filteredCountries = signal<DisplayCountry[]>([]);

  /**
   * Countries paired with their normalized name, recomputed only when the
   * country list changes rather than on every keystroke.
   */
  private readonly searchableCountries = computed(() =>
    this.countries().map(country => ({ country, searchName: normalizeForSearch(country.name) }))
  );
  autocompleteField = viewChild<AutocompleteFieldComponent<SearchCountry>>(AutocompleteFieldComponent);

  // ControlValueAccessor callbacks
  private onChange: (value: SearchCountry | null) => void = () => {};
  private onTouched: () => void = () => {};

  // ControlValueAccessor implementation
  writeValue(value: SearchCountry | null): void {
    const field = this.autocompleteField();
    if (field) {
      field.writeValue(value);
    }
  }

  registerOnChange(fn: (value: SearchCountry | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Forward to inner autocomplete field (handled by formControl binding)
  }

  private readonly priorityNames = ['Switzerland', 'United States', 'United Kingdom', 'Australia'];

  private withSeparator(results: SearchCountry[]): DisplayCountry[] {
    const priority = results.filter(c => this.priorityNames.includes(c.name));
    const rest = results.filter(c => !this.priorityNames.includes(c.name));
    if (priority.length === 0 || rest.length === 0) {
      return [...results];
    }
    return [
      ...priority.slice(0, -1),
      { ...priority[priority.length - 1], hasSeparatorAfter: true },
      ...rest,
    ];
  }

  // Event handlers
  onSearch(query: string): void {
    // Both sides are accent-folded, so "cao" matches Curaçao and Macao, and
    // "a" matches Åland Islands.
    const searchTerm = normalizeForSearch(query);
    const matched = this.searchableCountries()
      .filter(entry => entry.searchName.includes(searchTerm))
      .map(entry => entry.country);
    this.filteredCountries.set(this.withSeparator(matched));
  }

  onSelect(country: DisplayCountry): void {
    this.onChange(country);
    this.countrySelect.emit(country);
  }

  onClear(): void {
    this.onChange(null);
    this.hasNoValue.emit(true);
  }
}
